import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { toast } from "sonner"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { documentPermissionService } from "../infrastructure/document-permission-service"
import { GrantDocumentPermissionRequest } from "../dtos/DocumentPermissionDto"
import { BulkShareRequest } from "../dtos/BulkPermissionDtos"

export function useDocumentPermissions(documentId: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["doc-permissions", orgId, documentId],
    queryFn: () =>
      documentPermissionService.getDocumentPermissions(orgId, documentId),
    enabled: !!orgId && !!documentId,
  })
}

export function useGrantDocumentPermission(documentId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (request: GrantDocumentPermissionRequest) =>
      documentPermissionService.grantDocumentPermission(
        orgId,
        documentId,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["doc-permissions", orgId, documentId],
      })
      toast.success("Permission granted")
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status
        if (status === HttpStatusCodes.BAD_REQUEST) {
          toast.error(
            "Invalid request: provide either a user or a role, not both",
          )
        } else if (status === HttpStatusCodes.FORBIDDEN) {
          toast.error("Access denied")
        } else {
          toast.error("Failed to grant permission")
        }
      } else {
        toast.error("Failed to grant permission")
      }
    },
  })
}

export function useRevokeDocumentPermission(documentId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (permissionId: string) =>
      documentPermissionService.revokeDocumentPermission(orgId, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["doc-permissions", orgId, documentId],
      })
      toast.success("Permission revoked")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.NOT_FOUND) {
        toast.error("Permission not found")
      } else {
        toast.error("Failed to revoke permission")
      }
    },
  })
}

export function useBulkGrantDocumentPermissions(documentId: string) {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (request: BulkShareRequest) =>
      documentPermissionService.bulkGrantDocumentPermissions(
        orgId,
        documentId,
        request,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["doc-permissions", orgId, documentId],
      })
    },
    onError: () => {
      toast.error("Bulk grant failed")
    },
  })
}
